/**
 * Lightweight error reporter for Phase 0.
 *
 * Sends errors to the Cloudflare Worker `/errors` endpoint (apps/worker).
 * Endpoint URL is read from VITE_ERRORS_ENDPOINT at build time;
 * defaults to a no-op in dev when the env var is missing.
 */

const ENDPOINT = import.meta.env.VITE_ERRORS_ENDPOINT as string | undefined;

interface ErrorPayload {
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  timestamp: number;
  context?: Record<string, unknown>;
}

function send(payload: ErrorPayload): void {
  if (!ENDPOINT) {
    console.warn('[telemetry] VITE_ERRORS_ENDPOINT not configured', payload);
    return;
  }
  // fire-and-forget; do not block on network errors
  void fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch((err) => console.warn('[telemetry] failed to send', err));
}

export function reportError(error: unknown, context?: Record<string, unknown>): void {
  const err = error instanceof Error ? error : new Error(String(error));
  send({
    message: err.message,
    stack: err.stack,
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    timestamp: Date.now(),
    context,
  });
}

export function installGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, { kind: 'window.error' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { kind: 'unhandledrejection' });
  });
}
